/**
 * Hooks TanStack Query per FASE 5 — competizioni, classifica, partite, spareggi.
 *
 * Bridge v1.4 Sez. 4.10.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export type TipoCompetizione = "campionato" | "champions_league" | "supercoppa";
export type StatoCompetizione = "da_iniziare" | "in_corso" | "conclusa";
export type FasePartita =
  | "campionato"
  | "girone_a"
  | "girone_b"
  | "quarti_andata"
  | "quarti_ritorno"
  | "semi_andata"
  | "semi_ritorno"
  | "finale_andata"
  | "finale_ritorno"
  | "finale"
  | "supercoppa";
export type SpareggioMetodo =
  | "nessuno"
  | "diff_reti_torneo"
  | "fantapunti_torneo"
  | "fantapunti_partita"
  | "supplementari_panchina"
  | "sorteggio";

export interface CompetizioneItem {
  id: string;
  stagione_id: string;
  tipo: TipoCompetizione;
  nome: string;
  stato: StatoCompetizione;
  data_inizio: string | null;
  giornata_serie_a_inizio: number | null;
  giornata_serie_a_fine: number | null;
  struttura_torneo: string | null;
  cl_numero_gironi: number | null;
  cl_squadre_per_girone: number | null;
  tie_break_cascata: string[];
  congelata_at: string | null;
}

export interface PartitaItem {
  id: string;
  competizione_id: string;
  giornata_id: string | null;
  fase: FasePartita;
  squadra_casa_id: string;
  squadra_trasferta_id: string;
  gol_casa: number;
  gol_trasferta: number;
  punteggio_casa: number | null;
  punteggio_trasferta: number | null;
  versione: number;
  obsoleto: boolean;
  data_calcolo: string | null;
  spareggio_metodo_risoluzione: SpareggioMetodo;
  /** Risultato corretto a mano da un admin (Andrea 2026-09-15). */
  rettifica?: RettificaItem | null;
}

/** Modifica manuale del risultato di una partita (Andrea 2026-09-15). */
export interface RettificaItem {
  id: string;
  motivazione: string;
  creata_da: string | null;
  created_at: string;
  /** Il risultato del calcolo, cioe' quello senza la modifica. */
  calcolato_punteggio_casa: number | null;
  calcolato_punteggio_trasferta: number | null;
  calcolato_gol_casa: number;
  calcolato_gol_trasferta: number;
}

export interface ClassificaRow {
  squadra_id: string;
  posizione: number;
  punti: number;
  fantapunti_totali: string;
  gol_fantacalcio_fatti: number;
  gol_fantacalcio_subiti: number;
  diff_reti: number;
  partite_giocate: number;
  vinte: number;
  pareggiate: number;
  perse: number;
  /** Fantapunti fatti dalle avversarie (Andrea 2026-09-17). */
  fantapunti_subiti?: string;
}

export interface GironeItem {
  id: string;
  nome: string;
  partecipanti: Array<{
    squadra_id: string;
    testa_di_serie: boolean;
    posizione_classifica_precedente: number | null;
  }>;
}

export interface CreaCampionatoPayload {
  squadre_ids: string[];
  giornate_fanta_ids: string[];
  nome?: string;
  data_inizio?: string | null;
}

export interface CreaCLPayload {
  squadre_ids: string[];
  nome?: string;
  n_gironi?: number;
  squadre_per_girone?: number;
  composizione?: "sorteggio_random" | "teste_di_serie_classifica_precedente";
  classifica_precedente?: string[] | null;
  seed?: number | null;
  // Andrea 2026-09-01: struttura completa configurabile
  giornate_fanta_ids?: string[]; // ordinate: gironi poi fase KO
  qualificate_per_girone?: number; // 4 = tutte ai quarti, 2 = prime due in semi
  andata_ritorno_quarti?: boolean;
  andata_ritorno_semi?: boolean;
  finale_andata_ritorno?: boolean;
}

export interface AvanzaFaseCLResponse {
  fase_creata: string;
  partite_create: number;
  accoppiamenti: string[];
}

export interface CreaSupercoppaPayload {
  vincente_campionato_id: string;
  vincente_cl_id: string;
  nome?: string;
  data_inizio?: string | null;
}

// === Queries ===

export function useCompetizione(competizioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["competizione", competizioneId],
    queryFn: async () => {
      const res = await api.get<CompetizioneItem>(`/competizioni/${competizioneId}`);
      return res.data;
    },
    enabled: isAuth && !!competizioneId,
  });
}

export function usePartita(partitaId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["partita", partitaId],
    queryFn: async () => {
      const res = await api.get<PartitaItem>(`/partite/${partitaId}`);
      return res.data;
    },
    enabled: isAuth && !!partitaId,
  });
}

export function useCompetizioniStagione(stagioneId: string | null, tipo?: TipoCompetizione | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["competizioni-stagione", stagioneId, tipo ?? null],
    queryFn: async () => {
      const params = tipo ? `?tipo=${tipo}` : "";
      const res = await api.get<CompetizioneItem[]>(
        `/stagioni/${stagioneId}/competizioni${params}`,
      );
      return res.data;
    },
    enabled: isAuth && !!stagioneId,
  });
}

export function usePartiteCompetizione(
  competizioneId: string | null,
  opts?: { solo_attive?: boolean; fase?: FasePartita | null },
) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: [
      "partite-competizione",
      competizioneId,
      opts?.solo_attive ?? true,
      opts?.fase ?? null,
    ],
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set("solo_attive", String(opts?.solo_attive ?? true));
      if (opts?.fase) p.set("fase", opts.fase);
      const res = await api.get<PartitaItem[]>(
        `/competizioni/${competizioneId}/partite?${p.toString()}`,
      );
      return res.data;
    },
    enabled: isAuth && !!competizioneId,
  });
}

export function useClassifica(competizioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["classifica", competizioneId],
    queryFn: async () => {
      const res = await api.get<ClassificaRow[]>(`/competizioni/${competizioneId}/classifica`);
      return res.data;
    },
    enabled: isAuth && !!competizioneId,
  });
}

/** Turno della fase a eliminazione pianificato alla creazione della CL:
 * esiste anche PRIMA che le partite vengano sorteggiate, con gli
 * accoppiamenti generici ("1a Girone A vs 4a Girone B"). */
export interface TurnoKOPrevisto {
  fase: string;
  etichetta: string;
  giornata_id: string;
  turno: number;
  andata_ritorno: boolean;
  accoppiamenti: string[];
}

export function useGironiCL(competizioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["gironi-cl", competizioneId],
    queryFn: async () => {
      const res = await api.get<GironeItem[]>(`/competizioni/${competizioneId}/gironi-cl`);
      return res.data;
    },
    enabled: isAuth && !!competizioneId,
  });
}

export function usePianoKO(competizioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["piano-ko", competizioneId],
    queryFn: async () => {
      const res = await api.get<TurnoKOPrevisto[]>(`/competizioni/${competizioneId}/piano-ko`);
      return res.data;
    },
    enabled: isAuth && !!competizioneId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Andrea 2026-09-13: esito provvisorio della partita (vedi
 * GET /partite/{id}/simulazione). */
export interface GiocatoreSimulato {
  giocatore_id: string;
  nome: string;
  slot_ruolo: string | null;
  ruoli_mantra: string[];
  stato: "voto" | "sei_ufficio" | "sv" | "entrato";
  sostituisce_nome: string | null;
  voto_base: number | null;
  fantavoto: number | null;
  malus_fuori_ruolo: boolean;
  gol_fatti: number;
  assist: number;
  ammonizioni: number;
  espulsioni: number;
  autogol: number;
  gol_subiti: number;
  rigore_segnato: number;
  rigore_sbagliato: number;
  rigore_parato: number;
  porta_inviolata: boolean;
  capitano: boolean;
  vice: boolean;
  /** Voto base nella media del D-Factor (Andrea 2026-09-15). */
  in_d_factor?: boolean;
}

export interface SquadraSimulata {
  squadra_id: string;
  formazione_consegnata: boolean;
  nota: string | null;
  giocatori: GiocatoreSimulato[];
  punteggio_voto: number;
  bonus_capitano: number;
  capitano_bonus_nome: string | null;
  bonus_difesa: number;
  modificatore_difesa_attivo: boolean;
  bonus_fairplay: number;
  fairplay_attivo: boolean;
  punteggio_totale: number;
  gol: number;
  /** Modulo consegnato e modulo con cui si chiude (cambio modulo). */
  modulo: string | null;
  modulo_finale: string | null;
  /** Media del D-Factor; null se non si applica. */
  media_difesa?: number | null;
}

export interface SimulazionePartita {
  partita_id: string;
  giornata_id: string;
  calcolata_at: string;
  casa: SquadraSimulata;
  trasferta: SquadraSimulata;
  club_da_giocare: string[];
}

/** Calcolo "a secco" con i voti di adesso: niente cache, si rifà a ogni
 * apertura (il modal viene montato solo quando serve). */
export function useSimulazionePartita(partitaId: string | null, attivo: boolean) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["simulazione-partita", partitaId],
    queryFn: async () => {
      const res = await api.get<SimulazionePartita>(`/partite/${partitaId}/simulazione`);
      return res.data;
    },
    enabled: isAuth && !!partitaId && attivo,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Christian 2026-11-03: risultato provvisorio per la card «Giornata in
 * corso» della Home — stesso calcolo. Niente timer: i voti arrivano solo
 * con gli import a mano, il server ricalcola solo dopo un import e qui si
 * rilegge all'apertura / al rientro sulla Home. */
export function useSimulazioneLive(partitaId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["simulazione-live", partitaId],
    queryFn: async () => {
      const res = await api.get<SimulazionePartita>(`/partite/${partitaId}/simulazione`);
      return res.data;
    },
    enabled: isAuth && !!partitaId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/** Quanto il calcolo della giornata ha salvato per una squadra. */
export interface UfficialeSquadra {
  punteggio_voto: number;
  bonus_capitano: number;
  bonus_difesa: number;
  bonus_fairplay: number;
  punteggio_totale: number;
  gol: number;
}

export interface RecapSquadra extends SquadraSimulata {
  ufficiale: UfficialeSquadra | null;
}

export interface RecapPartitaData {
  partita_id: string;
  giornata_id: string;
  giornata_calcolata_at: string | null;
  casa: RecapSquadra;
  trasferta: RecapSquadra;
}

/** Recap a giornata calcolata (Andrea 2026-09-15): i conti del calcolo vero
 * coi voti finali, rifatti in sola lettura dal backend. */
export function useRecapPartita(partitaId: string | null, attivo: boolean) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["recap-partita", partitaId],
    queryFn: async () => (await api.get<RecapPartitaData>(`/partite/${partitaId}/recap`)).data,
    enabled: isAuth && !!partitaId && attivo,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// === Mutations ===

export function useCreaCampionato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      stagioneId,
      payload,
    }: {
      stagioneId: string;
      payload: CreaCampionatoPayload;
    }) => {
      const res = await api.post(`/admin/stagioni/${stagioneId}/competizioni/campionato`, payload);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["competizioni-stagione", vars.stagioneId],
      });
    },
  });
}

export function useAvanzaFaseCL() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ competizioneId }: { competizioneId: string; stagioneId: string }) => {
      const res = await api.post<AvanzaFaseCLResponse>(
        `/admin/competizioni/${competizioneId}/cl/avanza-fase`,
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["competizioni-stagione", vars.stagioneId],
      });
      qc.invalidateQueries({ queryKey: ["partite"] });
    },
  });
}

export function useCreaCL() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ stagioneId, payload }: { stagioneId: string; payload: CreaCLPayload }) => {
      const res = await api.post(`/admin/stagioni/${stagioneId}/competizioni/cl`, payload);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["competizioni-stagione", vars.stagioneId],
      });
    },
  });
}

export function useEliminaCompetizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ competizioneId }: { competizioneId: string; stagioneId: string }) => {
      await api.delete(`/admin/competizioni/${competizioneId}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["competizioni-stagione", vars.stagioneId],
      });
      qc.invalidateQueries({
        queryKey: ["stagione-competizioni-select", vars.stagioneId],
      });
    },
  });
}

export function useCreaSupercoppa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      stagioneId,
      payload,
    }: {
      stagioneId: string;
      payload: CreaSupercoppaPayload;
    }) => {
      const res = await api.post(`/admin/stagioni/${stagioneId}/competizioni/supercoppa`, payload);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["competizioni-stagione", vars.stagioneId],
      });
    },
  });
}

export function useRisolviSpareggio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partitaId, seed }: { partitaId: string; seed?: number | null }) => {
      const res = await api.post(`/admin/partite/${partitaId}/risolvi-spareggio`, {
        sorteggio_seed: seed,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["partite-competizione"] });
    },
  });
}

// ============================================================
// Modifica manuale del risultato (Andrea 2026-09-15)
// ============================================================

/** Partite di una giornata in tutte le competizioni. */
export function usePartiteGiornata(giornataId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["partite-giornata", giornataId],
    queryFn: async () => (await api.get<PartitaItem[]>(`/giornate/${giornataId}/partite`)).data,
    enabled: isAuth && !!giornataId,
  });
}

export interface RettificaPayload {
  punteggio_casa: number;
  punteggio_trasferta: number;
  gol_casa: number;
  gol_trasferta: number;
  motivazione: string;
}

/** Dopo una modifica cambiano partita, calendari, home e classifiche. */
function invalidaRisultatiPartite(qc: QueryClient) {
  for (const chiave of ["partita", "partite-competizione", "partite-giornata", "classifica"]) {
    qc.invalidateQueries({ queryKey: [chiave] });
  }
}

export function useRettificaPartita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partitaId, ...payload }: RettificaPayload & { partitaId: string }) =>
      (
        await api.put<{ partita: PartitaItem; avviso: string | null }>(
          `/admin/partite/${partitaId}/rettifica`,
          payload,
        )
      ).data,
    onSuccess: () => invalidaRisultatiPartite(qc),
  });
}

export function useTogliRettifica() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (partitaId: string) =>
      (await api.delete<PartitaItem>(`/admin/partite/${partitaId}/rettifica`)).data,
    onSuccess: () => invalidaRisultatiPartite(qc),
  });
}

/** Andrea 2026-10-08: campionato e coppa col nome scelto dalla lega. */
export function useRinominaCompetizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { competizioneId: string; stagioneId: string; nome: string }) =>
      (
        await api.patch<CompetizioneItem>(`/competizioni/${args.competizioneId}`, {
          nome: args.nome,
        })
      ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["competizioni-stagione", vars.stagioneId] });
      qc.invalidateQueries({ queryKey: ["competizione", vars.competizioneId] });
      qc.invalidateQueries({ queryKey: ["palmares"] });
    },
  });
}
